import {
  FilteringState,
  IntegratedFiltering,
  IntegratedSorting,
  SearchState,
  SortingState,
} from '@devexpress/dx-react-grid'
import {
  Grid as TableGrid,
  SearchPanel,
  TableFilterRow,
  TableHeaderRow,
  Toolbar,
  VirtualTable,
} from '@devexpress/dx-react-grid-material-ui'
import { Theme, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { alpha } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'
import { Table as TableBase } from '@devexpress/dx-react-grid'
import { UnitCancel } from '../../Units/reducer'
import { COLUMNS } from './config'
import { useCancelTableData } from './hooks/useTableData'

const getRowId = (row: UnitCancel) => `${row.subscriptionId}`

type CombinedCellProps = TableBase.CellProps & TableBase.DataCellProps

const formatStatus = (status?: string) => {
  if (!status) return ''
  return `${status.charAt(0).toUpperCase()}${status.slice(1)}`
}

const getStatusColor = (status: string | undefined, theme: Theme) => {
  switch (status?.toLowerCase()) {
    case 'paid':
      return theme.palette.success.dark
    case 'pending':
      return theme.palette.warning.dark
    case 'failed':
    case 'unpaid':
      return theme.palette.error.dark
    default:
      return 'inherit'
  }
}

interface Props {
  tableHeight?: number | string
  tableMinHeight?: number
}

export const AdminCancelListTableComponent: React.FC<Props> = ({
  tableHeight = '100%',
  tableMinHeight = 420,
}) => {
  const cancelTableData = useCancelTableData()
  const theme = useTheme<Theme>()

  const cellComponent = React.useCallback(
    (props: CombinedCellProps) => {
      const isStatusColumn = props.column.name === 'paymentStatus'
      const statusValue = isStatusColumn
        ? formatStatus(props.value as string | undefined)
        : props.value

      return (
        <VirtualTable.Cell
          {...props}
          value={statusValue}
          sx={{
            color: isStatusColumn
              ? getStatusColor(props.value as string | undefined, theme)
              : 'inherit',
            fontWeight: isStatusColumn ? 700 : 'inherit',
          }}
        >
          {statusValue}
        </VirtualTable.Cell>
      )
    },
    [theme]
  )

  return (
    <Paper
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: (theme: Theme) => alpha(theme.palette.background.paper, 0.72),
      }}
    >
      <Box
        sx={{
          py: 1,
          px: 2,
          bgcolor: (theme: Theme) => theme.palette.info.dark,
        }}
      >
        <Typography variant="h6">Cancellation List</Typography>
      </Box>
      <Paper
        sx={{
          height: tableHeight,
          flex: 1,
          minHeight: tableMinHeight,
          '& > div': {
            height: '100%',
          },
        }}
      >
        <TableGrid rows={cancelTableData} columns={COLUMNS} getRowId={getRowId}>
          <SearchState defaultValue="" />
          <FilteringState defaultFilters={[]} />
          <SortingState
            defaultSorting={[
              {
                columnName: 'lastPayment',
                direction: 'desc',
              },
            ]}
          />
          <IntegratedFiltering />
          <IntegratedSorting />
          <VirtualTable cellComponent={cellComponent} />
          <TableHeaderRow showSortingControls />
          <TableFilterRow />
          <Toolbar />
          <SearchPanel />
        </TableGrid>
      </Paper>
    </Paper>
  )
}
